import type {
    ClientStateApi,
    UserClientState,
    UserClientStatePatch,
} from "@codenexus/react";

/**
 * Empty client state, defined locally so we do not depend on the transitive
 * `@codenexus/shared` package (not a declared dependency of this app).
 */
export const EMPTY_CLIENT_STATE: UserClientState = {
    skills: {},
    acknowledgments: {},
};

function normalizeClientState(state: UserClientState | null | undefined): UserClientState {
    return {
        skills: state?.skills ?? {},
        acknowledgments: state?.acknowledgments ?? {},
    };
}

type StatusMap<T> = Record<string, T>;

function mergeStatusMap<T extends object>(
    base: StatusMap<T> | undefined,
    patch: StatusMap<T> | undefined,
): StatusMap<T> {
    const out: StatusMap<T> = { ...(base ?? {}) };
    for (const key of Object.keys(patch ?? {})) {
        out[key] = { ...(base?.[key] as object), ...(patch?.[key] as object) } as T;
    }
    return out;
}

function mergeClientState(
    base: UserClientState,
    patch: UserClientStatePatch,
): UserClientState {
    return {
        skills: mergeStatusMap(base.skills, patch.skills),
        acknowledgments: mergeStatusMap(base.acknowledgments, patch.acknowledgments),
    };
}

/**
 * Wraps a CodeNexus client-state API so transient/unavailable backends never
 * throw. The page-driven skill orchestration is best-effort UX state — if the
 * agent backend is old or down, the app should keep working silently rather
 * than spamming the console or breaking the chat panel.
 *
 * - `get()` falls back to the last known state (EMPTY on first load failure).
 * - `patch()` keeps the optimistic change locally so session UX stays consistent
 *   (e.g. welcome skills don't re-fire) even when persistence is unavailable.
 * - A single warning is logged so the degraded mode is still discoverable.
 */
export function createResilientClientStateApi(base: ClientStateApi): ClientStateApi {
    let mirror: UserClientState = EMPTY_CLIENT_STATE;
    let warned = false;

    const warnOnce = (op: string, err: unknown) => {
        if (warned) return;
        warned = true;
        console.warn(
            `[client-state] ${op} failed; continuing without server persistence ` +
                `(is the agent backend up to date?).`,
            err,
        );
    };

    return {
        async get() {
            try {
                mirror = normalizeClientState(await base.get());
                return mirror;
            } catch (err) {
                warnOnce("load", err);
                return mirror;
            }
        },
        async patch(partial) {
            // Apply optimistically to the local mirror first so the value sticks
            // for the session regardless of whether the server confirms it.
            mirror = mergeClientState(mirror, partial);
            try {
                const serverState = normalizeClientState(await base.patch(partial));
                mirror = mergeClientState(mirror, serverState);
            } catch (err) {
                warnOnce("save", err);
            }
            return mirror;
        },
    };
}
